import { IReview } from '../../../repositories/dailyrent/entities/response/Review';
import { IReply } from '../../../repositories/dailyrent/entities/response/Reply';
import { EType } from '../../../repositories/newbuilding-reviews/entities/common/UserSchema';
import { EStatus } from '../../../repositories/newbuilding-reviews/entities/common/LikeSchema';
import { IAnswerSchema } from '../../../repositories/newbuilding-reviews/entities/common/AnswerSchema';
import { IExtendedReviewWebSchema, TMappedDailyrentResponse } from '../../../types/reviews';
import { IPublicV1PageDataTripReviewsResponse } from '../../../repositories/dailyrent/entities/response/PublicV1PageDataTripReviewsResponse';
import { IObjectInfo } from '../../../types/objectInfo';
import { extractNumber } from '../../../utils/extractNumber';

const MODERATION_RULES_URL = 'https://www.cian.ru/help/about/rules-publication-reviews/';
const DEFAULT_REPLY_AUTHOR_TITLE = 'Собственник';

const mapAnswers = (newReply?: IReply | null): IAnswerSchema[] => {
  if (!newReply) {
    return [];
  }

  const answer: IAnswerSchema = {
    comment: {
      text: newReply.text,
      type: '',
    },
    date: newReply.replyDate,
    id: String(newReply.replyId),
    user: {
      id: -1,
      name: DEFAULT_REPLY_AUTHOR_TITLE, // Используем дефолтное значение, т.к. title удален
      avatarUrl: newReply.replyAuthorAvatarUrl,
      type: EType.Regular,
    },
    isAnonymous: false,
    isHidden: true,
    like: {
      status: EStatus.Unknown,
      likesCount: 0,
      dislikesCount: 0,
    },
  };

  return [answer];
};

const mapReview = (newReview: IReview): IExtendedReviewWebSchema => {
  const hasReply = !!newReview.reply;

  return {
    id: newReview.reviewId.toString(),
    user: {
      id: -1,
      name: newReview.authorName,
      avatarUrl: newReview.authorAvatarUrl,
      type: EType.Regular,
    },
    rating: Number(newReview.overallScore.replace(',', '.')),
    date: newReview.reviewDate,
    answersCountInt: Number(hasReply),
    answersCount: hasReply ? '1 ответ' : '',
    hasAnswers: hasReply,
    hasMoreAnswers: false,
    isUseful: false,
    isOnModeration: false,
    isLegacy: false,
    like: {
      status: EStatus.Unknown,
      likesCount: 0,
      dislikesCount: 0,
    },
    comment: {
      /** Это поле отображается в шапке отзыва */
      type: newReview.title || '',
      positive: newReview.pros,
      negative: newReview.cons,
    },
    labels: [],
    isAnonymous: false,
    answers: mapAnswers(newReview.reply),
    showAnswerButton: newReview.canReply,
  };
};

const mapObjectInfo = (responseData: IPublicV1PageDataTripReviewsResponse, offerId: number): IObjectInfo => ({
  fromDeveloperPropsCount: 0,
  objectId: offerId,
  objectName: responseData.header.offerTitle,
  formattedObjectName: `Отзывы о ${responseData.header.offerTitle}`,
  reviewDescription: [''],
  objectUrl: responseData.header.offerUrl,
  reviewsCount: extractNumber(responseData.ratingDetails?.reviewCount ?? undefined),
  reviewsCountText: responseData.ratingDetails?.reviewCount ?? '',
  questionsCount: 0,
  questionsCountText: '0 вопросов',
  rating: extractNumber(responseData.ratingDetails?.ratingValue),
  ratingText: '',
  sections: responseData.ratingDetails?.scores || [],
  seoData: {
    title: responseData.seoData?.headTitle,
    description: responseData.seoData?.description,
    location: responseData.header.address,
  },
  mlReview: null,
  builders: [],
  sellers: [],
});

export const mapPageDataTripReviews = (
  responseData: IPublicV1PageDataTripReviewsResponse,
  offerId: number,
): TMappedDailyrentResponse => {
  const reviews: IExtendedReviewWebSchema[] = responseData.reviews.map(mapReview);
  const objectInfo = mapObjectInfo(responseData, offerId);

  return {
    objectInfo,
    hasMore: false,
    reviews,
    moderationRulesUrl: MODERATION_RULES_URL,
    availableFilters: [],
    page: 0,
    tags: [],
    sort: [],
  };
};
